import React from 'react';
import moment from 'moment-timezone';
import mixpanel from 'mixpanel-browser';
import { FormattedMessage } from 'react-intl';
import { call } from '../utilities/http';
import { Snackbar } from '../components/snackbar';
import {
    LOADING,
    COMPLETE,
    TIME_GET_RECORDS_OK,
    TIME_UPDATE_RECORD_OK,
    TIME_DELETE_RECORD_OK,
    TIME_GET_JOB_PROJECTS_OK,
    TIME_GET_TIMESHEET_OK,
    TIME_GET_PENDING_TIMESHEET_OK,
    TIME_FIND_TIMESHEETS_OK,
    TIME_GET_TIMESHEETS_SUMMARY_OK,
    TIME_GET_TIMESHEETS_ALL_OK,
    TIME_GET_TIMESHEETS_APPROVED_OK,
    TIME_GET_TIMESHEETS_NOTAPPROVED_OK,
    TIME_GET_TIMESHEETS_PENDING_OK,
    TIME_GET_PENDING_SHIFTS_OK,
    TIME_GET_WORKER_TIMESHEET_SHIFTS_OK,
    TIME_GET_WORKER_TIMESHEET_BILLINGS_OK,
    TIME_GET_TIMESHEET_SHIFTS_OK,
    TIME_SUBMIT_TIMESHEET_OK,
    TIME_SUBMIT_ALL_OK,
    TIME_APPROVE_ALL_OK,
    TIME_SAVE_ENTRY_OK,
    TIME_SAVE_RECORD_OK,
    TIME_CONFIRM_OK,
    TIME_TIMESHEET_APPROVED,
    SAVING
} from './types';
import { timeObjectToMinutes } from '../utilities/time';
import { TIMESHEET_STATUS } from '../text/enumerations';
import { beginsWith } from '../utilities/string';

const defaultState = {
    records: undefined,
    projects: undefined,
    summary: undefined,
    timesheets: {
        approved: undefined,
        notapproved: undefined,
        pending: undefined,
        all: undefined
    },
    pendingShifts: undefined,
    pendingTimesheet: undefined,
    rangeTimesheets: undefined,
    workerShifts: undefined,
    workerExpenses: undefined,
    expensesDeliverables: undefined,
    timesheetShifts: {}
};

/**
 * Loading state mapper.
 * @param {object} state
 * @param {string} action
 */
export default function(state = defaultState, action) {
    switch (action.type) {
        case TIME_GET_RECORDS_OK:
            return { ...state, records: action.data };

        case TIME_UPDATE_RECORD_OK:
            mixpanel.track(TIME_UPDATE_RECORD_OK, action && action.data);
            return { ...state };

        case TIME_SAVE_ENTRY_OK:
        case TIME_SAVE_RECORD_OK:
            const { shift_id, timesheet_id, time_in, minutes } = action.data;
            let shiftData = { shift_id, timesheet_id, time_in, minutes };
            mixpanel.track(action.type, shiftData);
            return { ...state };

        case TIME_DELETE_RECORD_OK:
            return { ...state, records: state.records ? state.records.filter(r => r.id !== action.data) : [] };

        case TIME_GET_JOB_PROJECTS_OK:
            return { ...state, projects: action.data };

        case TIME_GET_TIMESHEETS_SUMMARY_OK:
            return { ...state, summary: action.data };

        case TIME_GET_TIMESHEETS_ALL_OK:
            if (!action.prop) {
                return { ...state, timesheets: { ...state.timesheets, all: action.data } };
            } else {
                const tsObj = { ...state.timesheets };
                tsObj[action.prop] = action.data;
                return { ...state, timesheets: tsObj };
            }

        case TIME_TIMESHEET_APPROVED:
            const approvedId = action.data;
            const submittedBefore = state.timesheets.submitted;
            if (!submittedBefore) return state;
            const approvedTimesheet = submittedBefore.find(t => t.id === approvedId);
            if (!approvedTimesheet) return state;

            approvedTimesheet.status = 'approved';
            const submitted = submittedBefore.filter(t => t.id !== approvedId);

            const approvedBefore = state.timesheets.approved;
            const approved = approvedBefore && [...approvedBefore, approvedTimesheet];

            const all = state.timesheets.all;

            return { ...state, timesheets: { ...state.timesheets, submitted, approved, all } };

        case TIME_GET_TIMESHEETS_APPROVED_OK:
            return { ...state, timesheets: { ...state.timesheets, approved: action.data.filter(t => t.status === 'approved') } };

        case TIME_GET_TIMESHEETS_NOTAPPROVED_OK:
            return { ...state, timesheets: { ...state.timesheets, notapproved: action.data.filter(t => t.status === 'notapproved') } };

        case TIME_GET_TIMESHEETS_PENDING_OK:
            return {
                ...state,
                timesheets: {
                    ...state.timesheets,
                    pending: action.data.filter(t => [TIMESHEET_STATUS.SUBMITTED, TIMESHEET_STATUS.RESUBMITTED].includes(t.status))
                }
            };

        case TIME_GET_PENDING_SHIFTS_OK:
            return { ...state, pendingShifts: action.data };

        case TIME_GET_TIMESHEET_SHIFTS_OK:
            let shifts = { ...state.timesheetShifts };
            shifts[action.id] = action.data;
            return { ...state, timesheetShifts: shifts };

        case TIME_GET_WORKER_TIMESHEET_SHIFTS_OK:
            return { ...state, workerShifts: action.data };

        case TIME_GET_WORKER_TIMESHEET_BILLINGS_OK:
            return { ...state, workerShifts: action.data.shifts, workerDeliverables: action.data.deliverables, workerExpenses: action.data.expenses };

        case TIME_GET_TIMESHEET_OK:
            let { id, data } = action.data;
            let timesheets = { ...state.timesheets };
            timesheets[id] = data;
            return { ...state, timesheets };

        case TIME_FIND_TIMESHEETS_OK:
            return { ...state, rangeTimesheets: action.data };

        case TIME_GET_PENDING_TIMESHEET_OK:
            return { ...state, pendingTimesheet: action.data };

        case TIME_SUBMIT_TIMESHEET_OK:
            return { ...state };

        case TIME_SUBMIT_ALL_OK:
            mixpanel.track(TIME_SUBMIT_ALL_OK, action.data);
            return { ...state };

        case TIME_APPROVE_ALL_OK:
            mixpanel.track(TIME_APPROVE_ALL_OK, action.data);
            return { ...state };

        case TIME_CONFIRM_OK:
            const { timesheetId } = action.data;
            const timesheet = state.rangeTimesheets.find(ts => ts.id === timesheetId);
            if (timesheet) timesheet.worker_confirmed_at = new Date();
            return { ...state };

        default:
            return state;
    }
}

export function saveTimeEntry(values, onSuccess = response => null) {
    const { id, job_id, project_id, timesheet_id, minutes, time_in } = values;
    let data = {
        id,
        job_id,
        project_id,
        timesheet_id,
        minutes: typeof minutes === 'number' ? minutes : timeObjectToMinutes(minutes),
        time_in: time_in.format()
    };

    let callDefinition = {
        path: id ? `/shift/${id}` : '/shift',
        method: id ? 'PUT' : 'POST',
        body: data
    };

    return async function(dispatch) {
        dispatch({ type: SAVING });
        call(callDefinition)
            .then(response => {
                const shift_id = id ? id : response.id;
                dispatch({ type: TIME_SAVE_ENTRY_OK, data: { ...data, shift_id } });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.saveTimeRecord'} defaultMessage={'Unable to save shift entry.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function saveTimeRecord(values, onSuccess = response => null) {
    const { job, project, timesheet_id, startTime, endTime, worker_notes, date } = values;
    let start = timeObjectToMinutes(startTime);
    let end = timeObjectToMinutes(endTime);
    let data = {
        job_id: job.id,
        project_id: project,
        timesheet_id,
        minutes: end - start,
        worker_notes,
        time_in: moment
            .tz(date, job.work_timezone)
            .startOf('day')
            .add(start, 'minutes')
            .format()
    };

    return async function(dispatch) {
        dispatch({ type: SAVING });
        call({ path: '/shift', method: 'POST', body: data })
            .then(response => {
                const shift_id = response.id;
                dispatch({ type: TIME_SAVE_RECORD_OK, data: { ...data, shift_id } });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.saveTimeRecord'} defaultMessage={'Unable to save shift entry.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function updateTimeRecord(values, onSuccess = response => null) {
    return async function(dispatch) {
        dispatch({ type: SAVING });
        call({ path: '/shift', method: 'PUT', body: {} })
            .then(response => {
                dispatch({ type: TIME_UPDATE_RECORD_OK, data: values });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.updateTimeRecord'} defaultMessage={'Unable to update shift entry.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}
export function deleteTimeRecord(id, onSuccess = response => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/shift/${id}`, method: 'DELETE' })
            .then(response => {
                dispatch({ type: TIME_DELETE_RECORD_OK, data: id });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.deleteTimeRecord'} defaultMessage={'Unable to delete shift entry.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function getTimeRecords(jobId, from, to, onSuccess = response => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/shift?job=${jobId}&from=${from.format()}&to=${to.format()}` })
            .then(response => {
                dispatch({ type: TIME_GET_RECORDS_OK, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.timeRecords'} defaultMessage={'Unable to retrieve timesheet entries.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

/**
 * Retrieves all job projects.
 * @param {Number} jobId
 */
export function getJobProjects(jobId, onSuccess = response => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/job/${jobId}/projects` })
            .then(response => {
                dispatch({ type: TIME_GET_JOB_PROJECTS_OK, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.jobProjects'} defaultMessage={'Unable to retrieve job projects.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function getPendingShifts(jobId, onSuccess = response => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/shift?job=${jobId}&status=pending` })
            .then(response => {
                dispatch({ type: TIME_GET_PENDING_SHIFTS_OK, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.getPendingShifts'} defaultMessage={'Unable to retrieve pending shift records.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function getWorkerShifts(timesheetId, onSuccess = response => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        if (!timesheetId) {
            dispatch({ type: TIME_GET_WORKER_TIMESHEET_SHIFTS_OK, data: [] });
            dispatch({ type: COMPLETE });
            onSuccess([]);
            return;
        }

        call({ path: `/timesheet/${timesheetId}/shifts` })
            .then(response => {
                dispatch({ type: TIME_GET_WORKER_TIMESHEET_SHIFTS_OK, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.getPendingShifts'} defaultMessage={'Unable to retrieve pending shift records.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function getWorkerBillings(timesheetId, onSuccess = response => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        if (!timesheetId) {
            dispatch({ type: TIME_GET_WORKER_TIMESHEET_BILLINGS_OK, data: { shitfs: [], deliverables: [] } });
            dispatch({ type: COMPLETE });
            onSuccess([]);
            return;
        }

        call({ path: `/timesheet/${timesheetId}/billings` })
            .then(response => {
                dispatch({ type: TIME_GET_WORKER_TIMESHEET_BILLINGS_OK, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.getPendingShifts'} defaultMessage={'Unable to retrieve pending shift records.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function saveDeliverable(billing, onSuccess = response => null) {
    const callDefinition = {
        path: `/deliverable/${billing.id || ''}`,
        method: billing.id ? 'PUT' : 'POST',
        body: billing
    };

    return async function(dispatch) {
        dispatch({ type: SAVING });
        call(callDefinition)
            .then(response => {
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.saveSoWBillingRecord'} defaultMessage={'Unable to save SoW billing entry.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function removeDeliverable(billingId, onSuccess = response => null) {
    const callDefinition = {
        path: `/deliverable/${billingId}`,
        method: 'DELETE'
    };

    return async function(dispatch) {
        dispatch({ type: SAVING });
        call(callDefinition)
            .then(response => {
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.deleteSoWBillingRecord'} defaultMessage={'Unable to remove SoW billing entry.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function saveExpense(billing, onSuccess = response => null) {
    const callDefinition = {
        path: `/expense/${billing.id || ''}`,
        method: billing.id ? 'PUT' : 'POST',
        body: billing
    };

    return async function(dispatch) {
        dispatch({ type: SAVING });
        call(callDefinition)
            .then(response => {
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.saveExpenseBillingRecord'} defaultMessage={'Unable to save Expense billing entry.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function removeExpense(billingId, onSuccess = response => null) {
    const callDefinition = {
        path: `/expense/${billingId}`,
        method: 'DELETE'
    };

    return async function(dispatch) {
        dispatch({ type: SAVING });
        call(callDefinition)
            .then(response => {
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.deleteSoWBillingRecord'} defaultMessage={'Unable to remove SoW billing entry.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function getTimesheetShifts(timesheetId, onSuccess = response => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/timesheet/${timesheetId}/shifts` })
            .then(response => {
                const shifts = response ? response.filter(s => !['archived', 'superseded'].includes(s.status)) : undefined;
                dispatch({ type: TIME_GET_TIMESHEET_SHIFTS_OK, data: shifts, id: timesheetId });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.getPendingShifts'} defaultMessage={'Unable to retrieve pending shift records.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function confirmTimesheet(timesheetId, onSuccess = response => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/timesheet/${timesheetId}/action/confirm`, method: 'POST' })
            .then(response => {
                dispatch({ type: TIME_CONFIRM_OK, data: { timesheetId } });
                dispatch({ type: COMPLETE });
                onSuccess(response);
                Snackbar.success(<FormattedMessage id={'success.confirmTimesheet'} defaultMessage={'Submitted.'} />);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.confirmTimesheet'} defaultMessage={'Unable to confirm timesheet.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function submitTimesheet(timesheetId, onSuccess = response => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ method: 'POST', path: `/timesheet/${timesheetId}/action/submit` })
            .then(response => {
                dispatch({ type: TIME_SUBMIT_TIMESHEET_OK, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
                Snackbar.success(<FormattedMessage id={'success.submitTimesheets'} defaultMessage={'Timesheet successfully submitted.'} />);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.submitTimesheets'} defaultMessage={'Unable to submit timesheet.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function resubmitTimesheet(timesheetId, onSuccess = response => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ method: 'POST', path: `/timesheet/${timesheetId}/action/resubmit` })
            .then(response => {
                dispatch({ type: TIME_SUBMIT_TIMESHEET_OK, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
                Snackbar.success(<FormattedMessage id={'success.resubmitTimesheet'} defaultMessage={'Timesheet successfully re-submitted.'} />);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.resubmitTimesheet'} defaultMessage={'Unable to submit timesheet.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function createTimesheet(jobId, periodEnd, onSuccess = response => null, pendingExistsCallback = () => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        const body = {
            job_id: jobId,
            period_ending: periodEnd.format()
        };
        call({ method: 'POST', path: `/timesheet`, body })
            .then(response => {
                dispatch({ type: TIME_GET_PENDING_TIMESHEET_OK, data: { ...body, id: response.id } });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                if (error && typeof error === 'string' && beginsWith(error, 'Error: There is already a pending timesheet for job') && pendingExistsCallback) {
                    pendingExistsCallback();
                } else {
                    Snackbar.error(<FormattedMessage id={'error.createTimesheet'} defaultMessage={'Unable to create timesheet record.'} />);
                }
                dispatch({ type: COMPLETE });
            });
    };
}

export function findTimesheets(jobId, from = undefined, to = undefined, onSuccess = response => null) {
    return async function(dispatch) {
        from = from || moment().add(-100, 'years');
        to = to || moment().add(100, 'years');

        dispatch({ type: LOADING });
        // call({ path: `/timesheet?job=${jobId}&from=${from.format()}&to=${to.format()}` })
        call({ path: `/timesheet?job=${jobId}&extended=true` })
            .then(response => {
                dispatch({ type: TIME_FIND_TIMESHEETS_OK, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.findTimesheets'} defaultMessage={'Unable to find timesheets.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function findPendingTimesheet(jobId, onSuccess = response => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/timesheet?job=${jobId}` })
            .then(response => {
                const timesheet =
                    response && response.length > 0
                        ? response.find(t => [TIMESHEET_STATUS.PENDING, TIMESHEET_STATUS.NOTAPPROVED].includes(t.status))
                        : undefined;
                if (timesheet) {
                    dispatch({ type: TIME_GET_PENDING_TIMESHEET_OK, data: timesheet });
                }

                dispatch({ type: COMPLETE });
                onSuccess(timesheet);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.findTimesheets'} defaultMessage={'Unable to find timesheets.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function getTimesheet(timesheetId, onSuccess = response => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/timesheet/${timesheetId}` })
            .then(response => {
                dispatch({ type: TIME_GET_TIMESHEET_OK, data: response, id: timesheetId });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.getTimesheet'} defaultMessage={'Unable to retrieve timesheet records.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function getApprovedTimesheets(clientId, filters = {}, onSuccess = response => null) {
    let timesheetFilters = {
        status: 'approved',
        from: moment()
            .add(-6, 'months')
            .format(),
        ...filters
    };
    return getTimesheets(clientId, timesheetFilters, TIME_GET_TIMESHEETS_APPROVED_OK, onSuccess);
}

export function getNotApprovedTimesheets(clientId, filters = {}, onSuccess = response => null) {
    let timesheetFilters = { status: 'notapproved', ...filters };
    return getTimesheets(clientId, timesheetFilters, TIME_GET_TIMESHEETS_NOTAPPROVED_OK, onSuccess);
}

export function getAllJobTimesheets(jobId, onSuccess = response => null) {
    return async function(dispatch) {
        call({ path: `/timesheet?job=${jobId}` })
            .then(response => {
                dispatch({ type: TIME_GET_TIMESHEETS_ALL_OK, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.getAllJobTimesheets'} defaultMessage={'Unable to retrieve timesheets.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function getPendingTimesheets(clientId, filters = {}, onSuccess = response => null) {
    return getTimesheets(clientId, filters, TIME_GET_TIMESHEETS_PENDING_OK, onSuccess);
}

export function getTimesheets(clientId, filters = undefined, type = undefined, onSuccess = response => null, prop = undefined, postFilters = undefined) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/client/${clientId}/timesheets`, queryParams: { extended: true, ...filters } })
            .then(response => {
                const timesheets = postFilters ? response.filter(postFilters) : response;
                dispatch({ type: type || TIME_GET_TIMESHEETS_ALL_OK, data: timesheets, prop });
                dispatch({ type: COMPLETE });
                onSuccess(timesheets);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.getTimesheets'} defaultMessage={'Unable to retrieve timesheets.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function getAdminTimesheets(adminId, filters = undefined, type = undefined, onSuccess = response => null, postFilters = undefined) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/admin/${adminId}/timesheets`, queryParams: { extended: true, ...filters } })
            .then(response => {
                const data = postFilters ? response.filter(postFilters) : response;
                dispatch({ type: TIME_GET_TIMESHEETS_ALL_OK, data, prop: type });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.getTimesheets'} defaultMessage={'Unable to retrieve timesheets.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function approveTimesheet(id, approver_notes, onSuccess = response => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/timesheet/${id}/action/approve`, method: 'POST', body: { approver_notes } })
            .then(response => {
                dispatch({ type: COMPLETE });
                dispatch({ type: TIME_TIMESHEET_APPROVED, data: id });
                onSuccess(response);
                Snackbar.success(<FormattedMessage id={'success.approveTimesheets'} defaultMessage={'Timesheet approved.'} />);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.approveTimesheets'} defaultMessage={'Unable to approve timesheets.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function rejectTimesheet(id, approver_notes, onSuccess = response => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/timesheet/${id}/action/notapprove`, method: 'POST', body: { approver_notes } })
            .then(response => {
                dispatch({ type: COMPLETE });
                onSuccess(response);
                Snackbar.success(<FormattedMessage id={'success.rejectTimesheet'} defaultMessage={'Timesheet rejected.'} />);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.rejectTimesheets'} defaultMessage={'Unable to reject timesheets.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function updateTimesheet(timesheet, onSuccess = response => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/timesheet/${timesheet.id}`, method: 'PUT', body: timesheet })
            .then(response => {
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.updateTimesheets'} defaultMessage={'Unable to update timesheets.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function submitAllTimesheets(adminId, clientId = undefined, onSuccess = response => null) {
    const filters = {
        admin: adminId
    };
    if (clientId) {
        filters.client = clientId;
    }
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/timesheets/submit_all`, method: 'POST', queryParams: { ...filters } })
            .then(response => {
                dispatch({ type: TIME_SUBMIT_ALL_OK, data: { adminId, clientId } });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.submitAllTimesheets'} defaultMessage={'Unable to submit timesheets.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function approveAllTimesheets(adminId, clientId = undefined, onSuccess = response => null) {
    const filters = {
        admin: adminId
    };
    if (clientId) {
        filters.client = clientId;
    }

    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/timesheets/approve_all`, method: 'POST', queryParams: { ...filters } })
            .then(response => {
                dispatch({ type: TIME_APPROVE_ALL_OK, data: { adminId, clientId } });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.approveAllTimesheets'} defaultMessage={'Unable to approve timesheets.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}

export function getTimesheetSummary(clientId, hmId = undefined, onSuccess = response => null) {
    const params = hmId ? { queryParams: { hm_id: hmId } } : {};
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/client/${clientId}/timesheets_summary`, ...params })
            .then(response => {
                dispatch({ type: TIME_GET_TIMESHEETS_SUMMARY_OK, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.getTimesheetSummary'} defaultMessage={'Unable to retrieve timesheets summary.'} />);
                dispatch({ type: COMPLETE });
            });
    };
}



// WEBPACK FOOTER //
// ./src/reducers/timekeeping.jsx